import React from 'react';
import {
  AddressBook, Bank, Buildings, CalendarBlank, CalendarCheck, Car, ChartDonut, ChartPieSlice, ClockCounterClockwise, Cloud,
  CrownSimple, CurrencyEur, Door, EnvelopeSimpleOpen, Flame, FolderSimple, Gauge, GearSix, GlobeHemisphereWest, Hammer, Image,
  Key, Leaf, MapPin, MapPinArea, NotePencil, PiggyBank, Receipt, Ruler, ShieldCheck, Signature, Sparkle, SquaresFour, Ticket, TrendUp,
  Umbrella, UserCircleCheck, UsersThree, Warehouse, Storefront, Wrench, type Icon,
} from '@phosphor-icons/react';
import { Widget, WidgetWert, useRasterMass } from '../../../../components/immobilien/ui';
import { ANGEBUNDENE_PANELS } from '../../../../components/immobilien/objektPanels';
import type { ObjektWidgetDef } from '../../../../components/immobilien/objektWidgets';
import { eur, heuteIso, initialen, qm } from '../../../../lib/immobilien/format';
import { deDatum, ehemaligeMieter, type EinheitArt } from '../../../../lib/immobilien/objektDetail';
import { kontaktName } from '../../../../lib/objekte/objektKontakte';
import { BUCHHALTUNG_FOLGT } from '../../../../lib/immobilien/hinweise';
import { fotoHintergrund, sichereUrl } from '../../../../lib/immobilien/url';
import { useObjektSeite } from './ObjektSeitenKontext';
import { istWohnend } from '../../../../lib/dashboard/leerstand';
import { analyseMieterhoehung } from '../../../../lib/immobilien/mieterhoehung';
import { schluesselJeEinheit } from '../../../../lib/immobilien/schluessel';
import { baueAktivitaeten } from '../../../../lib/immobilien/aktivitaet';
import { istTicketOffen, ticketStatusLabel } from '../../../../lib/immobilien/tickets';

export const RUND = "ui-rounded,'SF Pro Rounded',-apple-system,sans-serif";

const VA_LANG: Record<string, string> = { MV: 'Mietverwaltung', WEG: 'WEG-Verwaltung', SEV: 'SEV-Verwaltung', GV: 'Gewerbeverwaltung' };

function Wert({ text, farbe = 'var(--ink)', groesse = 28 }: { text: React.ReactNode; farbe?: string; groesse?: number }) {
  return <span className="immo-zahl" style={{ fontFamily: RUND, fontSize: groesse, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1, color: farbe }}>{text}</span>;
}

export function Leer({ text }: { text: string }) {
  return <span className="flex-1 flex items-center justify-center text-center" style={{ fontSize: 12, color: 'var(--ink3)', padding: '0 6px' }}>{text}</span>;
}

/** Hinweis auf noch nicht angebundene Bereiche (kein toter Knopf). */
export function Folgt({ text = 'Wird noch angebunden' }: { text?: string }) {
  return (
    <div className="flex flex-col items-start" style={{ marginTop: 'auto', gap: 6 }}>
      {/* wie Chip xs, darf in schmalen Widgets aber umbrechen statt abgeschnitten zu werden */}
      <span style={{ maxWidth: '100%', minHeight: 20, padding: '3px 8px', borderRadius: 10, fontSize: 10, fontWeight: 800, lineHeight: 1.4, background: 'var(--fill2)', color: 'var(--ink2)' }}>{text}</span>
    </div>
  );
}

export function Ring({ prozent, farbe, spur, groesse = 86, mitte }: { prozent: number | null; farbe: string; spur: string; groesse?: number; mitte: React.ReactNode }) {
  const umfang = 2 * Math.PI * 50;
  const anteil = prozent === null ? 0 : Math.max(0, Math.min(100, prozent)) / 100;
  return (
    <div className="relative shrink-0" style={{ width: groesse, height: groesse }}>
      <svg width={groesse} height={groesse} viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }} aria-hidden>
        <circle cx="60" cy="60" r="50" fill="none" stroke={spur} strokeWidth="14" />
        {anteil > 0 && <circle cx="60" cy="60" r="50" fill="none" stroke={farbe} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${umfang * anteil} ${umfang}`} />}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{mitte}</div>
    </div>
  );
}

const ART_ICON: Record<EinheitArt, Icon> = { wohnung: Door, gewerbe: Storefront, stellplatz: Car, sonstiges: Warehouse };

function InfoZeile({ icon: IconKomp, farbe, flaeche, k, v }: { icon: Icon; farbe: string; flaeche: string; k: string; v: string }) {
  return (
    <div className="flex items-center min-w-0" style={{ gap: 9 }}>
      <span className="flex items-center justify-center shrink-0" style={{ width: 30, height: 30, borderRadius: '50%', background: flaeche }}>
        <IconKomp weight="fill" size={14} color={farbe} />
      </span>
      <div className="flex flex-col min-w-0" style={{ gap: 1 }}>
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{k}</span>
        <span className="truncate" style={{ fontSize: 13, fontWeight: 600 }}>{v || '–'}</span>
      </div>
    </div>
  );
}

/**
 * Ein Objekt-Widget nach seinem Schlüssel.
 * gesperrt: Klick öffnet nichts (Bearbeiten-Modus). gedimmt: ausgeblendetes Widget im Bearbeiten-Modus.
 */
export function ObjektWidget({ def, gesperrt, gedimmt = false }: { def: ObjektWidgetDef; gesperrt: boolean; gedimmt?: boolean }) {
  const el = ObjektWidgetInhalt({ def, gesperrt });
  if (!gedimmt || !React.isValidElement<{ style?: React.CSSProperties }>(el)) return el;
  return React.cloneElement(el, { style: { ...el.props.style, opacity: 0.4 } });
}

function ObjektWidgetInhalt({ def, gesperrt }: { def: ObjektWidgetDef; gesperrt: boolean }): React.ReactElement | null {
  const s = useObjektSeite();
  const mass = useRasterMass();
  const { daten, kennzahlen: k, uebersicht } = s;
  const o = daten.objekt;
  const angebunden = def.panel === 'einheiten' || (def.panel !== null && ANGEBUNDENE_PANELS.has(def.panel));
  const klick = gesperrt || !angebunden ? undefined
    : def.panel === 'einheiten' ? () => s.zuEinheiten()
      : def.panel ? () => s.oeffnePanel(def.panel as Exclude<typeof def.panel, 'einheiten' | null>) : undefined;
  const layoutId = def.panel && def.panel !== 'einheiten' ? `objekt-panel-${def.panel}` : undefined;
  const basis = { titel: def.label, groesse: def.groesse, onOeffnen: klick, layoutId } as const;

  switch (def.key) {
    case 'hero': {
      const foto = fotoHintergrund(o.bild_url);
      const link = sichereUrl(o.onedrive_link);
      return (
        <Widget {...basis} titel="Objektfoto" icon={Image} ohneKopf onOeffnen={undefined}
          style={{ background: foto ?? 'var(--fill)', boxShadow: 'inset 0 1px 0 var(--line), 0 12px 30px -8px var(--shade)', backdropFilter: 'none' }}>
          {!foto && <div aria-hidden className="absolute inset-0" style={{ background: 'repeating-linear-gradient(135deg,var(--fill2) 0 14px,var(--fill) 14px 28px)' }} />}
          {!foto && <Buildings aria-hidden weight="duotone" size={44} color="var(--ink3)" className="absolute" style={{ top: 'calc(50% - 40px)', left: 'calc(50% - 22px)' }} />}
          <div className="relative flex justify-between" style={{ gap: 8 }}>
            <button type="button" onClick={() => s.oeffnePanel('stamm', { bearbeiten: true })} className="flex items-center cursor-pointer"
              style={{ height: 26, padding: '0 10px', borderRadius: 13, border: 'none', gap: 5, fontSize: 12, fontWeight: 600, background: 'var(--chip)', color: 'var(--chip-ink)', backdropFilter: 'blur(14px)' }}>
              <Image weight="fill" size={13} color="var(--acc)" />Foto ändern
            </button>
            {link ? (
              <a href={link} target="_blank" rel="noreferrer" className="flex items-center"
                style={{ height: 26, padding: '0 10px', borderRadius: 13, gap: 5, fontSize: 12, fontWeight: 600, background: 'var(--chip)', color: 'var(--chip-ink)', backdropFilter: 'blur(14px)', textDecoration: 'none' }}>
                <Cloud weight="fill" size={13} color="var(--info)" />OneDrive
              </a>
            ) : (
              <span title="Kein OneDrive-Link hinterlegt (Stammdaten → Bearbeiten)" className="flex items-center"
                style={{ height: 26, padding: '0 10px', borderRadius: 13, gap: 5, fontSize: 12, fontWeight: 600, background: 'var(--chip)', color: 'var(--ink3)', backdropFilter: 'blur(14px)' }}>
                <Cloud size={13} />Kein OneDrive
              </span>
            )}
          </div>
          <div className="relative grid" style={{ marginTop: 'auto', borderRadius: 18, padding: '12px 14px', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 10, background: 'rgba(20,20,22,0.55)', backdropFilter: 'blur(24px) saturate(160%)', boxShadow: 'inset 0 0 0 1px var(--line)', color: '#fff' }}>
            {[['Baujahr', o.baujahr ? String(o.baujahr) : '–'], ['Objekttyp', o.objekttyp || '–'], ['Verwaltung', s.verwaltungsart ? VA_LANG[s.verwaltungsart] ?? s.verwaltungsart : '–']].map(([kk, v]) => (
              <div key={kk} className="flex flex-col min-w-0" style={{ gap: 1 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{kk}</span>
                <span className="truncate" style={{ fontSize: 13, fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </div>
        </Widget>
      );
    }
    case 'Stamm':
      return (
        <Widget {...basis} icon={GearSix} rechts={o.objektnummer ? `Nr. ${o.objektnummer}` : undefined}>
          <div className="grid flex-1 content-center" style={{ gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: '10px 14px' }}>
            <InfoZeile icon={Flame} farbe="var(--warn)" flaeche="var(--warn-soft)" k="Heizung" v={o.heizungsart ?? ''} />
            <InfoZeile icon={Leaf} farbe="var(--ok)" flaeche="var(--ok-soft)" k="Energie" v={[o.energieklasse, o.energieausweis].filter(Boolean).join(' · ')} />
            <InfoZeile icon={Car} farbe="var(--info)" flaeche="var(--info-soft)" k="Parken"
              v={o.stellplatz_typ && o.stellplatz_typ !== 'Keine' ? `${o.stellplatz_anzahl || 1}× ${o.stellplatz_typ}` : 'Keine'} />
            <InfoZeile icon={Hammer} farbe="var(--acc)" flaeche="var(--acc-soft)" k="Baujahr" v={o.baujahr ? String(o.baujahr) : ''} />
          </div>
        </Widget>
      );
    case 'Beleg':
      return (
        <Widget {...basis} icon={ChartDonut}>
          <div className="flex-1 flex items-center justify-center">
            <Ring prozent={k.einheiten ? k.belegungProzent : null} farbe="var(--acc)" spur="var(--acc-soft)"
              mitte={<span className="immo-zahl" style={{ fontFamily: RUND, fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em' }}>{k.einheiten ? `${k.belegungProzent}%` : '–'}</span>} />
          </div>
          <span className="text-center" style={{ fontSize: 12, color: 'var(--ink3)' }}>{k.vermietet} von {k.einheiten} vermietet</span>
        </Widget>
      );
    case 'Karte': {
      const strasse = [o.strasse, o.hausnummer].filter(Boolean).join(' ') || s.name;
      return (
        <Widget {...basis} icon={MapPin} ohneKopf style={{ background: 'repeating-linear-gradient(45deg,var(--fill2) 0 12px,var(--fill) 12px 24px)', boxShadow: 'inset 0 1px 0 var(--line), 0 12px 30px -8px var(--shade)', backdropFilter: 'none' }}>
          <span className="absolute flex items-center" style={{ top: 12, left: 12, height: 24, padding: '0 10px', borderRadius: 12, gap: 5, fontSize: 11, fontWeight: 600, background: 'var(--chip)', color: 'var(--chip-ink)', backdropFilter: 'blur(12px)' }}>
            <MapPin weight="fill" size={12} color="var(--acc)" />{strasse}
          </span>
          <span className="flex items-center justify-center" style={{ margin: 'auto', width: 42, height: 42, borderRadius: '50%', background: 'var(--acc-fill)', boxShadow: '0 0 0 4px rgba(255,255,255,0.15), 0 8px 20px var(--shade)' }}>
            <Buildings weight="fill" size={19} color="var(--acc-ink)" />
          </span>
          <span className="absolute text-center" style={{ bottom: 10, left: 0, right: 0, fontSize: 11, color: 'var(--ink3)' }}>Lage & Karte</span>
        </Widget>
      );
    }
    case 'Flaeche':
      return (
        <Widget {...basis} icon={Ruler}>
          <WidgetWert wert={k.flaeche > 0 ? qm(Math.round(k.flaeche)) : '–'} sub={k.kaltProQm !== null ? `Ø ${eur(k.kaltProQm)} / m²` : 'Keine vermietete Fläche'} />
        </Widget>
      );
    case 'Einheiten': {
      // so viele Kacheln, wie das Widget im Raster Platz hat (eine Kachel je Spalte und Zeile)
      const spalten = Math.max(2, mass?.w ?? 2);
      const zeilen = Math.max(1, mass?.h ?? 2);
      const sichtbar = uebersicht.slice(0, spalten * zeilen);
      return (
        <Widget {...basis} icon={SquaresFour} rechts={`${k.einheiten} ${k.einheiten === 1 ? 'Einheit' : 'Einheiten'}`}>
          {uebersicht.length === 0 ? <Leer text="Noch keine Einheiten angelegt." /> : (
            <>
              <div className="grid flex-1 min-h-0" style={{ marginTop: 12, gridTemplateColumns: `repeat(${spalten},minmax(0,1fr))`, gridTemplateRows: `repeat(${zeilen},minmax(0,1fr))`, gap: 8 }}>
                {sichtbar.map(e => {
                  const IconKomp = ART_ICON[e.art];
                  return (
                    <div key={e.id} className="flex flex-col justify-between min-w-0" style={{ borderRadius: 18, padding: '11px 12px', background: 'var(--fill)' }}>
                      <div className="flex justify-between items-start">
                        <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: '50%', background: e.vermietet ? 'var(--fill2)' : 'var(--warn-soft)' }}>
                          <IconKomp weight="fill" size={15} color={e.vermietet ? 'var(--acc)' : 'var(--warn)'} />
                        </span>
                        <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 700, color: e.vermietet ? 'var(--ok)' : 'var(--warn)' }}>
                          {e.vermietet ? eur(e.gesamt, { vorzeichen: true, nachkomma: 0 }) : 'frei'}
                        </span>
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{e.bezeichnung}</span>
                        <span className="truncate" style={{ fontSize: 11, color: e.vermietet ? 'var(--ink3)' : 'var(--warn)' }}>{e.vermietet ? e.mieter.join(', ') : 'Leerstand'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {uebersicht.length > sichtbar.length && <span style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: 'var(--acc)' }}>+ {uebersicht.length - sichtbar.length} weitere Einheiten</span>}
            </>
          )}
        </Widget>
      );
    }
    case 'Kalt':
      return (
        <Widget {...basis} icon={CurrencyEur} akzent>
          <WidgetWert akzent wert={eur(k.kaltSumme)} sub={k.kaltProEinheit !== null ? `Ø ${eur(k.kaltProEinheit, { nachkomma: 0 })} / Whg` : 'Keine Vermietung'} />
        </Widget>
      );
    case 'Ehem': {
      const ehemalige = ehemaligeMieter(daten.mieter, daten.einheiten).length;
      return (
        <Widget {...basis} icon={ClockCounterClockwise}>
          <WidgetWert wert={ehemalige} sub={ehemalige === 1 ? 'ehemaliger Mieter' : 'ehemalige Mieter'} />
        </Widget>
      );
    }
    case 'Weg': {
      const meaSumme = daten.weg.reduce((x, w) => x + (w.mea ?? 0), 0);
      const hausgeld = daten.weg.reduce((x, w) => x + (w.hausgeld_vorschuss ?? 0), 0);
      return (
        <Widget {...basis} icon={ChartPieSlice} rechts={`${daten.weg.length} Einheiten`}>
          <WidgetWert wert={eur(hausgeld)} sub={`Hausgeld / Monat · MEA ${meaSumme.toLocaleString('de-DE')}`} />
        </Widget>
      );
    }
    case 'Zust': {
      const name = (o.zustaendig ?? '').trim() || 'Global';
      return (
        <Widget {...basis} icon={UserCircleCheck}>
          <div className="flex-1 flex flex-col items-center justify-center min-w-0" style={{ gap: 6 }}>
            <span className="flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: '50%', background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 14, fontWeight: 600 }}>
              {name === 'Global' ? <GlobeHemisphereWest weight="fill" size={20} /> : initialen(name)}
            </span>
            <span className="truncate max-w-full" style={{ fontSize: 12, fontWeight: 600 }}>{name}</span>
          </div>
          <span className="text-center" style={{ fontSize: 11, color: 'var(--ink3)' }}>{o.zustaendig_typ === 'team' ? 'Team' : name === 'Global' ? 'Alle' : 'Mitarbeiter'}</span>
        </Widget>
      );
    }
    case 'Eigent': {
      const liste = daten.eigentuemer;
      return (
        <Widget {...basis} icon={CrownSimple} rechts={liste.length ? `${liste.length} ${liste.length === 1 ? 'Eigentümer' : 'Eigentümer'}` : undefined}>
          {liste.length === 0 ? <Leer text="Kein Eigentümer verknüpft." /> : (
            <div className="flex flex-1 items-center" style={{ gap: 12, minWidth: 0 }}>
              {liste.slice(0, 3).map(e => {
                const n = kontaktName(e);
                return (
                  <div key={e.id} className="flex items-center min-w-0" style={{ gap: 10, flex: 1 }}>
                    <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--knob)', fontSize: 13, fontWeight: 600 }}>{initialen(n)}</span>
                    <div className="flex flex-col min-w-0">
                      <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{n}</span>
                      <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{e.email || e.telefon || 'Eigentümer'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Widget>
      );
    }
    case 'Notizen': {
      const letzte = daten.notizen[0];
      return (
        <Widget {...basis} icon={NotePencil} rechts={daten.notizen.length ? String(daten.notizen.length) : undefined}>
          {letzte ? <span className="flex-1 overflow-hidden" style={{ marginTop: 10, fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: 'var(--ink2)' }}>{letzte.text}</span>
            : <Leer text="Noch keine Notizen." />}
        </Widget>
      );
    }
    case 'Aliase':
      return (
        <Widget {...basis} icon={Sparkle}>
          <div className="flex flex-1 flex-wrap content-center overflow-hidden" style={{ gap: 5, minHeight: 0 }}>
            {daten.aliase.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Keine Aliase</span>}
            {daten.aliase.slice(0, 6).map(a => (
              <span key={a.id} className="flex items-center whitespace-nowrap" style={{ height: 24, padding: '0 9px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: 'var(--acc-soft)', color: 'var(--acc)' }}>{a.alias}</span>
            ))}
          </div>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{daten.aliase.length} {daten.aliase.length === 1 ? 'Alias' : 'Aliase'} für die E-Mail-Zuordnung</span>
        </Widget>
      );
    case 'Termine': {
      const jetzt = new Date().toISOString();
      const naechster = daten.termine.find(t => t.start_datum >= jetzt && (t.status ?? '') !== 'abgesagt');
      return (
        <Widget {...basis} icon={CalendarBlank}>
          {naechster ? (
            <div className="flex flex-col" style={{ marginTop: 'auto', gap: 3 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{deDatum(naechster.start_datum)}</span>
              <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{naechster.titel || 'Termin'}</span>
            </div>
          ) : <Leer text="Keine anstehenden Termine." />}
          {!angebunden && <Folgt />}
        </Widget>
      );
    }
    case 'Zaehler': {
      const aktiv = daten.zaehler.filter(z => z.aktiv !== false);
      return (
        <Widget {...basis} icon={Gauge} rechts={`${aktiv.length} aktiv`}>
          {aktiv.length === 0 ? <Leer text="Keine Zähler erfasst." /> : (
            <div className="flex flex-wrap content-center flex-1" style={{ gap: 6 }}>
              {aktiv.slice(0, 6).map(z => (
                <span key={z.id} className="flex items-center whitespace-nowrap" style={{ height: 28, padding: '0 10px', borderRadius: 14, gap: 6, fontSize: 12, fontWeight: 600, background: 'var(--fill2)' }}>
                  <Gauge weight="fill" size={13} color="var(--acc)" />{z.typ || 'Zähler'}{z.zaehler_nummer ? ` · ${z.zaehler_nummer}` : ''}
                </span>
              ))}
            </div>
          )}
          {!angebunden && <Folgt />}
        </Widget>
      );
    }
    case 'Dokumente':
      return (
        <Widget {...basis} icon={FolderSimple} rechts={`${daten.dokumenteAnzahl} Dateien`}>
          {daten.dokumente.length === 0 ? <Leer text="Noch keine Dokumente." /> : (
            <div className="grid flex-1 items-center" style={{ gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 8 }}>
              {daten.dokumente.slice(0, 4).map(d => (
                <div key={d.id} className="flex flex-col items-center min-w-0" style={{ gap: 6 }}>
                  <span className="relative flex items-end justify-center" style={{ width: 40, height: 50, borderRadius: 8, paddingBottom: 6, background: 'linear-gradient(180deg,#f4f4f6,#d9d9de)', boxShadow: '0 4px 10px var(--shade)' }}>
                    <span style={{ fontSize: 8, fontWeight: 800, color: 'var(--bad)' }}>{(d.datei_typ ?? d.datei_name?.split('.').pop() ?? 'DOC').slice(0, 4).toUpperCase().replace('APPLICATION/', '')}</span>
                  </span>
                  <span className="truncate max-w-full" style={{ fontSize: 10, fontWeight: 500, color: 'var(--ink2)' }}>{d.titel || d.datei_name || 'Dokument'}</span>
                </div>
              ))}
            </div>
          )}
          {!angebunden && <Folgt />}
        </Widget>
      );
    case 'Kontakte': {
      const erster = daten.kontakte[0];
      return (
        <Widget {...basis} icon={AddressBook} rechts={daten.kontakte.length ? String(daten.kontakte.length) : undefined}>
          {erster ? (
            <div className="flex-1 flex flex-col items-center justify-center min-w-0" style={{ gap: 6 }}>
              <span className="flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: '50%', background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 14, fontWeight: 600 }}>{initialen(kontaktName(erster))}</span>
              <span className="truncate max-w-full" style={{ fontSize: 12, fontWeight: 700 }}>{kontaktName(erster)}</span>
            </div>
          ) : <Leer text="Keine Kontakte verknüpft." />}
          {!angebunden && <Folgt />}
        </Widget>
      );
    }
    case 'Dienstleister':
      return (
        <Widget {...basis} icon={Wrench} rechts={`${daten.dienstleister.length} zugeordnet`}>
          {daten.dienstleister.length === 0 ? <Leer text="Keine Dienstleister zugeordnet." /> : (
            <div className="flex flex-col flex-1 justify-center" style={{ gap: 6 }}>
              {daten.dienstleister.slice(0, 3).map(d => (
                <div key={d.id} className="flex items-center min-w-0" style={{ gap: 8, fontSize: 13 }}>
                  <Wrench weight="fill" size={14} color="var(--acc)" />
                  <span className="truncate flex-1" style={{ fontWeight: 600 }}>{d.name}</span>
                  <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)', maxWidth: '45%' }}>{d.kategorien.map(x => x.name).join(', ')}</span>
                </div>
              ))}
            </div>
          )}
          {!angebunden && <Folgt />}
        </Widget>
      );
    // ── Mit der Buchhaltung (Mietkonto, Bank, Mahnwesen) ──────────────────
    case 'Mieten': return <Widget {...basis} icon={CalendarCheck}><Leer text="Mieteingang des Monats" /><Folgt text={BUCHHALTUNG_FOLGT} /></Widget>;
    case 'Mahn': return <Widget {...basis} icon={EnvelopeSimpleOpen}><Folgt text={BUCHHALTUNG_FOLGT} /></Widget>;
    case 'Finanzen': return <Widget {...basis} icon={Receipt}><Leer text="Buchhaltung aus Immoware" /></Widget>;
    case 'Bank': return <Widget {...basis} icon={Bank}><Folgt text={BUCHHALTUNG_FOLGT} /></Widget>;
    case 'Mieterh': {
      const heute = heuteIso();
      const laufend = uebersicht.filter(e => e.art === 'wohnung').flatMap(e => daten.mieter.filter(m => m.einheit_id === e.id && istWohnend(m.status)).map(m => ({ m, f: e.flaeche })));
      const moeglich = laufend.filter(({ m, f }) => { const a = analyseMieterhoehung(m, heute, f); return a.fruehestesVerlangen <= heute && a.spielraum > 0 && !a.geplanteAenderung; });
      return (
        <Widget {...basis} icon={TrendUp}>
          <WidgetWert wert={laufend.length ? `${moeglich.length} / ${laufend.length}` : '–'} sub={laufend.length ? 'Erhöhung möglich (§ 558)' : 'Keine Vermietung'} farbe={moeglich.length ? 'var(--ok)' : undefined} />
        </Widget>
      );
    }
    case 'Keys': {
      const staende = [...schluesselJeEinheit(daten.uebergaben).values()];
      const beiMietern = staende.filter(x => x.art === 'einzug').reduce((n, x) => n + x.summe, 0);
      return (
        <Widget {...basis} icon={Key}>
          <WidgetWert wert={staende.length ? beiMietern : '–'} sub={staende.length ? `bei Mietern · ${staende.length} Einheiten erfasst` : 'Noch keine Übergabe'} />
        </Widget>
      );
    }
    case 'Wplan': {
      const rl = daten.weg.reduce((x, w) => x + (w.ruecklage_vorschuss ?? 0), 0);
      return (
        <Widget {...basis} icon={PiggyBank}>
          <WidgetWert wert={daten.weg.length ? eur(rl * 12, { nachkomma: 0 }) : '–'} sub="Rücklage-Zuführung / Jahr" />
        </Widget>
      );
    }
    case 'Grund': return <Widget {...basis} icon={MapPinArea}><Folgt /></Widget>;
    case 'Tickets': {
      const offen = daten.tickets.filter(t => istTicketOffen(t.status));
      return (
        <Widget {...basis} icon={Ticket} rechts={`${offen.length} offen`}>
          {offen.length === 0 ? <Leer text="Keine offenen Tickets." /> : (
            <div className="flex flex-col flex-1 justify-center" style={{ gap: 6 }}>
              {offen.slice(0, 3).map(t => (
                <div key={t.id} className="flex items-center min-w-0" style={{ gap: 8, fontSize: 13 }}>
                  <Ticket weight="fill" size={14} color={t.eskaliert || t.status === 'eskaliert' ? 'var(--bad)' : 'var(--acc)'} />
                  <span className="truncate flex-1" style={{ fontWeight: 600 }}>{t.titel || 'Ticket'}</span>
                  <span className="shrink-0" style={{ fontSize: 11, color: 'var(--ink3)' }}>{ticketStatusLabel(t.status)}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      );
    }
    case 'Wartung': {
      const jetzt = heuteIso();
      const naechste = daten.termine.find(t => t.typ === 'wartung' && t.start_datum.slice(0, 10) >= jetzt && (t.status ?? '') !== 'abgesagt');
      return (
        <Widget {...basis} icon={ShieldCheck}>
          <WidgetWert wert={<span style={{ fontSize: 20 }}>{naechste ? deDatum(naechste.start_datum) : o.letzte_pruefung ? deDatum(o.letzte_pruefung) : '–'}</span>}
            sub={naechste ? 'nächste Wartung' : o.letzte_pruefung ? 'letzte Prüfung' : 'Keine Prüfung erfasst'} />
        </Widget>
      );
    }
    case 'Vertraege':
      return (
        <Widget {...basis} icon={Signature}>
          <WidgetWert wert={daten.vertraege.length + daten.dienstleister.length} sub={`${daten.vertraege.length} Mietverträge · ${daten.dienstleister.length} Dienstleister`} />
        </Widget>
      );
    case 'Versich': {
      const n = daten.fachDokumente.filter(d => d.kategorie === 'Versicherung').length;
      return (
        <Widget {...basis} icon={Umbrella}>
          <WidgetWert wert={n} sub={n === 1 ? 'Versicherungsdokument' : 'Versicherungsdokumente'} />
        </Widget>
      );
    }
    case 'Etv': {
      const jetzt = heuteIso();
      const naechste = daten.termine.find(t => t.typ === 'meeting' && t.start_datum.slice(0, 10) >= jetzt && (t.status ?? '') !== 'abgesagt');
      return (
        <Widget {...basis} icon={UsersThree}>
          <WidgetWert wert={<span style={{ fontSize: 20 }}>{naechste ? deDatum(naechste.start_datum) : '–'}</span>} sub={naechste ? 'nächste Versammlung' : 'Keine Versammlung geplant'} />
        </Widget>
      );
    }
    case 'Protokoll': {
      const liste = baueAktivitaeten({ notizen: daten.notizen, dokumente: daten.dokumente.slice(0, 10), tickets: daten.tickets.slice(0, 10), termine: daten.termine }, new Date().toISOString(), 3);
      return (
        <Widget {...basis} icon={ClockCounterClockwise}>
          {liste.length === 0 ? <Leer text="Noch keine Aktivitäten." /> : (
            <div className="flex flex-col flex-1 justify-center" style={{ gap: 6 }}>
              {liste.map((a, i) => (
                <div key={`${a.id ?? i}`} className="flex items-center min-w-0" style={{ gap: 8, fontSize: 13 }}>
                  <span className="shrink-0 immo-zahl" style={{ fontSize: 11, color: 'var(--ink3)', width: 70 }}>{deDatum(a.datum)}</span>
                  <span className="truncate flex-1" style={{ fontWeight: 600 }}>{a.titel}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      );
    }
    default: return null;
  }
}
